import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Send, Loader2, X, AlertCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { getCurrencySymbol } from '../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NominateWinnerDialog
 *
 * Props:
 *   open, onOpenChange
 *   estimates: [{id, name, total_effort, total_cost}]  // the 2+ estimates being compared
 *   defaultWinnerId: string                             // preselected
 *   onNominated(result)                                 // called after successful POST
 */
export default function NominateWinnerDialog({
  open, onOpenChange, estimates = [], defaultWinnerId = '', onNominated,
}) {
  const [winnerId, setWinnerId] = useState(defaultWinnerId || estimates[0]?.id || '');
  const [stakeholderEmail, setStakeholderEmail] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) {
      setWinnerId(defaultWinnerId || estimates[0]?.id || '');
      setStakeholderEmail('');
      setStakeholderName('');
      setNote('');
    }
  }, [open, defaultWinnerId, estimates]);

  const loserIds = estimates.filter((e) => e.id !== winnerId).map((e) => e.id);

  const handleSubmit = async () => {
    if (!winnerId) return;
    if (!stakeholderEmail) {
      toast.error('Stakeholder email is required');
      return;
    }
    if (!loserIds.length) {
      toast.error('Nomination needs at least one loser estimate to archive');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/compare/winner/nominate`, {
        winner_id: winnerId,
        loser_ids: loserIds,
        stakeholder_email: stakeholderEmail,
        stakeholder_name: stakeholderName,
        note,
        send_email: true,
      }, { withCredentials: true });
      toast.success(res.data?.message || 'Nomination sent');
      onNominated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Nomination failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="nominate-winner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Nominate the winning estimate
          </DialogTitle>
          <DialogDescription>
            Send this comparison to a stakeholder for sign-off. On approval, the winner is
            marked as canonical and the other scenarios are archived with a diff-log entry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-slate-700 mb-2 block">Winner</Label>
            <RadioGroup value={winnerId} onValueChange={setWinnerId} data-testid="nominate-winner-radiogroup">
              {estimates.map((e) => (
                <label
                  key={e.id}
                  htmlFor={`winner-radio-${e.id}`}
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                    winnerId === e.id
                      ? 'border-amber-400 bg-amber-50/40'
                      : 'border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`winner-option-${e.id}`}
                >
                  <RadioGroupItem id={`winner-radio-${e.id}`} value={e.id} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{e.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {e.total_effort != null ? `${Math.round(e.total_effort)} hrs · ` : ''}
                      {e.total_cost != null ? `${getCurrencySymbol(e.currency_code)}${Math.round(e.total_cost).toLocaleString()}` : ''}
                    </div>
                  </div>
                  {winnerId === e.id && (
                    <Trophy className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  )}
                </label>
              ))}
            </RadioGroup>
            {loserIds.length > 0 && (
              <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                On approval, {loserIds.length} other estimate{loserIds.length > 1 ? 's' : ''} will be archived (soft — not deleted).
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="stakeholder-email" className="text-xs font-medium text-slate-700">Stakeholder email *</Label>
              <Input
                id="stakeholder-email"
                type="email"
                placeholder="sponsor@client.com"
                value={stakeholderEmail}
                onChange={(e) => setStakeholderEmail(e.target.value)}
                className="mt-1"
                data-testid="nominate-stakeholder-email"
              />
            </div>
            <div>
              <Label htmlFor="stakeholder-name" className="text-xs font-medium text-slate-700">Name (optional)</Label>
              <Input
                id="stakeholder-name"
                placeholder="Jane Sponsor"
                value={stakeholderName}
                onChange={(e) => setStakeholderName(e.target.value)}
                className="mt-1"
                data-testid="nominate-stakeholder-name"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="nominate-note" className="text-xs font-medium text-slate-700">Note for the approver</Label>
            <Textarea
              id="nominate-note"
              placeholder="Why this estimate — context, constraints, timing…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="mt-1"
              data-testid="nominate-note"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="nominate-cancel-btn">
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !stakeholderEmail || !winnerId} data-testid="nominate-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            {submitting ? 'Sending…' : 'Send for approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
